import React, { useState, useEffect, useRef } from 'react'
import {
  User,
  LogOut,
  ChevronDown,
  HelpCircle,
  IdCardIcon,
  Globe,
  Check,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchUserProfile, UserProfile } from '@/lib/user/profile'
// Avatar components not needed - using CdnImage directly
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import { Link } from '@tanstack/react-router'
import { useToast } from '@/components/ui/use-toast'
import { useTranslation } from 'react-i18next'
import api from '@/lib/axios'
import { useTourManager } from '@/hooks/useTourManager'
// API_URL not needed - CdnImage handles URL formatting
import { useUserStore } from '@/store/userStore'

interface UserProfileMenuProps {
  onLogout: () => void
}

const UserProfileMenu: React.FC<UserProfileMenuProps> = ({ onLogout }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showTourButton, setShowTourButton] = useState(false)
  const [isLanguageOpen, setIsLanguageOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { toast } = useToast()
  const { t, i18n } = useTranslation('common')
  const queryClient = useQueryClient()
  const { updateTourStatus } = useTourManager()

  // formatAvatarUrl removed - CdnImage handles URL formatting automatically

  // Use React Query to fetch user data - this will auto-update when profile changes
  // With HTTP-only cookies, we try to fetch and let the backend validate the cookie
  // If the cookie is invalid, the request will fail with 401 and we handle it gracefully
  const {
    data: userData,
    isLoading,
    refetch,
  } = useQuery<UserProfile>({
    queryKey: ['userProfile'],
    queryFn: async () => {
      return fetchUserProfile()
    },
    retry: false, // Don't retry on 401 - cookie is invalid
    staleTime: 0, // Always refetch when query is invalidated
  })

  // Get user from userStore (fetched from API)
  const userStore = useUserStore((state) => state.user)

  useEffect(() => {
    // Check if any tours are in progress
    const checkTourStatus = () => {
      const adminTourActive =
        localStorage.getItem('tour_progress_admin') !== null
      const dataStorageTourActive =
        localStorage.getItem('tour_progress_dataStorage') !== null
      const timetableTourActive =
        localStorage.getItem('tour_progress_timetable') !== null
      const crossRouteTourActive =
        localStorage.getItem('cross_route_tour_progress') !== null

      setShowTourButton(
        adminTourActive ||
          dataStorageTourActive ||
          timetableTourActive ||
          crossRouteTourActive
      )
    }

    // Check tour status initially
    checkTourStatus()

    // Listen for custom login event to refresh user data
    const handleUserLogin = () => {
      refetch()
    }

    // Listen for tour status changes
    const handleTourStatusChange = () => {
      checkTourStatus()
    }

    window.addEventListener('user-login', handleUserLogin)
    window.addEventListener('tour-status-change', handleTourStatusChange)

    return () => {
      window.removeEventListener('user-login', handleUserLogin)
      window.removeEventListener('tour-status-change', handleTourStatusChange)
    }
  }, [refetch])

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Function to finish all tours
  const handleFinishTour = async () => {
    // Update database to mark tours as completed
    await updateTourStatus(false)

    // Dispatch event to notify other components
    window.dispatchEvent(new CustomEvent('tour-status-change'))

    // Close dropdown
    setIsOpen(false)

    // Show toast
    toast({
      title: t('userProfileMenu.tours.completedTitle'),
      description: t('userProfileMenu.tours.completedDescription'),
      duration: 3000,
    })

    // Hide tour button
    setShowTourButton(false)
  }

  // Language update mutation (EN/FR only)
  const updateLanguageMutation = useMutation({
    mutationFn: async (code: 'EN' | 'FR') => {
      const res = await api.patch('/user-controller/updatelanguage', {
        language: code,
      })
      return String(res.data?.language || code).toUpperCase() as 'EN' | 'FR'
    },
    onSuccess: (newCode) => {
      const lng = newCode === 'FR' ? 'fr' : 'en'
      if (!i18n.language?.startsWith(lng)) {
        i18n.changeLanguage(lng)
      }
      const userId = userStore?.id || userData?.id
      queryClient.setQueryData(
        ['userLanguage', userId],
        newCode
      )
    },
  })

  const setLanguage = (code: 'EN' | 'FR') => {
    const lng = code === 'FR' ? 'fr' : 'en'
    if (!i18n.language?.startsWith(lng)) {
      i18n.changeLanguage(lng)
    }
    updateLanguageMutation.mutate(code)
    setIsLanguageOpen(false)
    setIsOpen(false)
  }

  // Use fetched data from API or userStore (no localStorage fallback)
  const displayUser = userData || (userStore ? {
    firstname: userStore.firstname,
    lastname: userStore.lastname,
    fullname: userStore.fullname || `${userStore.firstname} ${userStore.lastname}`,
    avatar: userStore.avatar,
    email: userStore.email,
    role: userStore.role,
  } as UserProfile : null)

  // Debug: Log avatar information
  useEffect(() => {
    if (displayUser) {
      // console.log('UserProfileMenu - User data:', {
      //   hasAvatar: !!displayUser.avatar,
      //   avatar: displayUser.avatar,
      //   userDataAvatar: userData?.avatar,
      //   userStoreAvatar: userStore?.avatar,
      // })
    }
  }, [displayUser, userData, userStore])

  // Show loading state only if we have no data at all
  if (isLoading && !displayUser) {
    return (
      <div className="relative admin-profile-menu">
        <div className="profile-button flex items-center space-x-2">
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
          <div className="hidden sm:block h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    )
  }

  // If we have no user data at all (not even from localStorage), show minimal fallback
  if (!displayUser) {
    return (
      <div className="relative admin-profile-menu">
        <div className="profile-button flex items-center space-x-2">
          <div className="h-10 w-10 rounded-full border-2 border-gray-200 bg-gray-500 flex items-center justify-center text-white font-bold text-sm">
            ??
          </div>
          <span className="hidden sm:block text-sm font-medium text-gray-700">
            {t('userProfileMenu.user')}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="relative admin-profile-menu" ref={dropdownRef}>
      <button
        id="admin-profile-button"
        onClick={toggleDropdown}
        className="profile-button flex items-center space-x-2 focus:outline-none"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar using CdnImage for better CDN handling */}
        <div className="h-10 w-10 rounded-full border-2 border-gray-200 overflow-hidden flex-shrink-0">
          {displayUser.avatar && displayUser.avatar.trim() !== '' ? (
            <CdnImage
              src={displayUser.avatar}
              alt={displayUser.fullname || `${displayUser.firstname || ''} ${displayUser.lastname || ''}`.trim() || 'User'}
              className="h-full w-full object-cover"
              fallbackSrc={DEFAULT_AVATAR}
            />
          ) : (
            <div className="h-full w-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
              {(
                (displayUser.firstname?.[0] || '') + (displayUser.lastname?.[0] || '')
              ).toUpperCase() ||
                displayUser.fullname?.substring(0, 2).toUpperCase() ||
                'US'}
            </div>
          )}
        </div>
        <span className="hidden sm:block text-sm font-medium text-gray-700">
          {displayUser.fullname || `${displayUser.firstname || ''} ${displayUser.lastname || ''}`.trim() || 'User'}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1">
            {/* User info section */}
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900">
                {displayUser.fullname ||
                  `${displayUser.firstname || ''} ${displayUser.lastname || ''}`.trim()}
              </p>
              <p className="text-sm text-gray-500 truncate">{displayUser.email}</p>
              <p className="text-xs text-gray-400 mt-1 capitalize">
                {displayUser.role}
              </p>
            </div>

            {/* Navigation links */}
            <Link
              to="/profile"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <User className="mr-3 h-4 w-4 text-gray-500" />
              {t('userProfileMenu.links.profile')}
            </Link>
            {!['SuperAdmin', 'Parent'].includes(
              userStore?.role || userData?.role || ''
            ) && (
              <Link
                to="/card"
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <IdCardIcon className="mr-3 h-4 w-4 text-gray-500" />
                {t('userProfileMenu.links.card')}
              </Link>
            )}
            {/* Settings link hidden */}
            {/* <Link
              to="/settings"
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Settings className="mr-3 h-4 w-4 text-gray-500" />
              {t('userProfileMenu.links.settings')}
            </Link> */}

            <div className="border-t border-gray-100"></div>

            {showTourButton && (
              <button
                onClick={handleFinishTour}
                className="flex w-full items-center px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
              >
                <HelpCircle className="mr-3 h-4 w-4 text-blue-500" />
                {t('userProfileMenu.tours.finish')}
              </button>
            )}

            {/* Language submenu */}
            <div className="border-t border-gray-100"></div>
            <button
              onClick={() => setIsLanguageOpen((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <span className="flex items-center">
                <Globe className="mr-3 h-4 w-4 text-gray-500" />
                {t('userProfileMenu.language.title')}
              </span>
              <span className="flex items-center space-x-2">
                <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                  {i18n.language?.startsWith('fr')
                    ? t('userProfileMenu.language.french')
                    : t('userProfileMenu.language.english')}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${
                    isLanguageOpen ? 'rotate-180' : ''
                  }`}
                />
              </span>
            </button>
            {isLanguageOpen && (
              <div className="py-1">
                <button
                  onClick={() => setLanguage('EN')}
                  className="flex w-full items-center justify-between pl-11 pr-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <span>{t('userProfileMenu.language.english')}</span>
                  {i18n.language?.startsWith('en') && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </button>
                <button
                  onClick={() => setLanguage('FR')}
                  className="flex w-full items-center justify-between pl-11 pr-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <span>{t('userProfileMenu.language.french')}</span>
                  {i18n.language?.startsWith('fr') && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </button>
              </div>
            )}

            <button
              onClick={onLogout}
              className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
            >
              <LogOut className="mr-3 h-4 w-4 text-red-500" />
              {t('userProfileMenu.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserProfileMenu
